import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RequestContactUpdateState, CustomFields, CommonFields } from "@requestContactUpdate/models";
import { RequestType } from "../constants";
import { hasValidationErrors } from "../utils/validation";
import { sendRequestContactUpdateThunk, submitFormThunk } from "./thunks";

const requiredFieldsPath = ["commonFields.contactName", "commonFields.contactEmail", "commonFields.message"];

const initialState: RequestContactUpdateState = {
  open: false,
  loading: false,
  validationWarningShown: false,
  submitSuccess: false,
  successDialogOpen: false,
  requestType: "",
  commonFields: {
    contactName: "",
    contactEmail: "",
    selectedAccounts: [],
    selectedFunds: [],
    message: "",
    attachment: null,
  },
  customFields: {},
  draft: {
    loading: false,
    attachmentId: null,
    attachmentName: null,
  },
};

const requestContactUpdateSlice = createSlice({
  name: "requestContactUpdate",
  initialState,
  reducers: {
    openRequestContactUpdateDialog: (
      state,
      action: PayloadAction<{ requestType: typeof RequestType; customFields?: CustomFields } | undefined>
    ) => {
      state.open = true;
      state.submitSuccess = false;
      state.validationWarningShown = false;
      state.successDialogOpen = false;
      state.requestType = RequestType;

      if (!action.payload) return;

      if (action.payload.customFields) {
        state.customFields = action.payload.customFields;

        state.commonFields.selectedAccounts = action.payload.customFields.filterParamsInvestorAccounts;
        state.commonFields.selectedFunds = action.payload.customFields.filterParamsFunds;
      }
    },
    closeRequestContactUpdateDialog(state) {
      state.open = false;
    },
    openSuccessDialog(state) {
      state.successDialogOpen = true;
    },
    closeSuccessDialog(state) {
      state.successDialogOpen = false;
    },
    updateCommonRequestData: (state, action: PayloadAction<Partial<CommonFields>>) => {
      state.commonFields = {
        ...state.commonFields,
        ...action.payload,
      };

      if (state.validationWarningShown) {
        state.validationWarningShown = hasValidationErrors(state, requiredFieldsPath);
      }
    },
    validateFields: (state) => {
      state.validationWarningShown = hasValidationErrors(state, requiredFieldsPath);
    },
    resetRequestContactUpdateState: () => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(submitFormThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(submitFormThunk.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(submitFormThunk.rejected, (state) => {
        state.loading = false;
      })
      .addCase(sendRequestContactUpdateThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(sendRequestContactUpdateThunk.fulfilled, (state) => {
        state.loading = false;
        state.submitSuccess = true;
        state.open = false;
        state.successDialogOpen = true;
      })
      .addCase(sendRequestContactUpdateThunk.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const {
  openRequestContactUpdateDialog,
  closeRequestContactUpdateDialog,
  resetRequestContactUpdateState,
  updateCommonRequestData,
  validateFields,
  openSuccessDialog,
  closeSuccessDialog,
} = requestContactUpdateSlice.actions;
export default requestContactUpdateSlice.reducer;
